import { useEffect, useState } from 'react';
import { useScript } from 'usehooks-ts';

export const googleMapKey = process.env['STORYBOOK_GMAPS_API'] || process.env['NEXT_PUBLIC_GMAPS_API'];

/**
 * The one URL every Maps consumer loads. A page that wants Maps downloading before its bundle
 * (`<script async>` in its HTML, see employer-app `_document.tsx`) must use this very string:
 * `useScript` looks the tag up by `src`, so the same URL means it reuses that tag instead of
 * adding a second one ("You have included the Google Maps JavaScript API multiple times").
 */
export const gmapsScriptUrl = (libraries: string, key: string | undefined = googleMapKey) =>
  `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=${libraries}&region=IT`;

// A script tag rendered in the HTML carries this attribute, so `useScript` reads a known status
// from it instead of `null`.
export const GMAPS_SCRIPT_STATUS_ATTRIBUTE = 'data-status';

export const useGmapsScript = ({ libraries }: { libraries: string }) => {
  // Only the library names matter to the Maps JS loader. Some call sites append a `types=`
  // hint (consumed later by the Places Autocomplete request via `placesFilterTypes`, not by
  // the loader). Keeping it in the URL made each caller's `src` unique, so `useScript` loaded
  // the whole Maps API multiple times ("You have included the Google Maps JavaScript API
  // multiple times…"). Strip everything after the library names so every caller resolves to a
  // single stable URL and Maps loads exactly once.
  const libraryNames = libraries.split('&')[0];
  const script = gmapsScriptUrl(libraryNames);
  const gmapsScript = useScript(script);
  // When the HTML started the script and it finished before React mounted, `useScript` finds the
  // tag, reads `data-status="loading"` and waits for a `load` event that already fired. The API
  // object is the proof of loading that needs no event: if it is there, Maps is ready.
  const [isLoadedOutsideReact, setIsLoadedOutsideReact] = useState(false);
  useEffect(() => {
    if (gmapsScript === 'ready' || isLoadedOutsideReact) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((window as any).google?.maps) setIsLoadedOutsideReact(true);
  }, [gmapsScript, isLoadedOutsideReact]);
  useEffect(() => {
    if (gmapsScript === 'error') {
      // eslint-disable-next-line no-console
      console.error('Error loading GMAPS Script');
    }
  }, [gmapsScript]);
  return gmapsScript === 'ready' || isLoadedOutsideReact;
};
